import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { expenseAPI, adminAPI } from '../../services/api';

/**
 * Finance Hub — NEW admin tab. Purely additive: it reads the platform revenue
 * the app already computes (adminAPI.getFinancialDashboard) and combines it with
 * the new Expense collection to show true Net Profit, plus full expense
 * management (add/edit/void), date-wise, with category analytics + CSV export.
 * It does not modify any existing money flow.
 */

const CATEGORY_LABELS = {
  salaries_wages: 'Salaries & Wages',
  infrastructure_servers: 'Infrastructure / Servers',
  marketing_advertising: 'Marketing & Advertising',
  rent_utilities: 'Rent & Utilities',
  software_subscriptions: 'Software Subscriptions',
  payment_gateway_fees: 'Payment Gateway Fees',
  logistics_operations: 'Logistics & Operations',
  legal_professional: 'Legal & Professional',
  delivery_subsidies: 'Delivery Subsidies',
  refunds_absorbed: 'Refunds Absorbed',
  office_supplies: 'Office Supplies',
  travel: 'Travel',
  taxes_licenses: 'Taxes & Licenses',
  miscellaneous: 'Miscellaneous'
};

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const catLabel = (c) => CATEGORY_LABELS[c] || c;

// Default range = current month.
function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { startDate: iso(start), endDate: iso(now) };
}

const emptyForm = {
  category: 'infrastructure_servers', subcategory: '', description: '', amount: '',
  gstApplicable: false, gstRate: '', gstAmount: '',
  vendorName: '', vendorGstin: '', paymentMethod: 'bank_transfer', paymentStatus: 'paid',
  expenseDate: new Date().toISOString().slice(0, 10), notes: '',
  isRecurring: false, frequency: 'monthly'
};

const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
const th = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', fontSize: 13, color: '#555' };
const td = { padding: '10px 12px', borderBottom: '1px solid #f2f2f2', fontSize: 14 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 14 });
const kgrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 };

const KpiGrid = ({ items }) => (
  <div style={kgrid}>
    {items.filter(Boolean).map((k) => (
      <div style={card} key={k.label}>
        <div style={{ color: '#777', fontSize: 12 }}>{k.label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: k.color || '#111' }}>{k.value}</div>
        {k.sub != null && k.sub !== '' && <div style={{ fontSize: 11, color: '#999' }}>{k.sub}</div>}
      </div>
    ))}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <h3 style={{ margin: '10px 0 8px', fontSize: 15, color: '#444' }}>{title}</h3>
    {children}
  </div>
);

function FinanceHub() {
  const [activeTab, setActiveTab] = useState('overview');
  const [range, setRange] = useState(monthRange());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [summary, setSummary] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [projectMonths, setProjectMonths] = useState(3);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [allPartners, setAllPartners] = useState([]);

  const [expenses, setExpenses] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, pages: 1, total: 0 });
  const [filters, setFilters] = useState({ category: '', status: '' });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const params = useCallback(() => ({ startDate: range.startDate, endDate: range.endDate }), [range]);

  // Overview + Analytics data: expense summary + reused platform revenue.
  const loadSummary = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [sumRes, dashRes, restRes, partRes] = await Promise.allSettled([
        expenseAPI.getSummary(params()),
        adminAPI.getFinancialDashboard(params()),
        expenseAPI.getRestaurants(params()),
        expenseAPI.getDeliveryPartners(params())
      ]);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data.data);
      else setError('Failed to load expense summary');
      // Revenue is a REUSE of the existing dashboard; tolerate its absence.
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data.data);
      else setDashboard(null);
      setAllRestaurants(restRes.status === 'fulfilled' ? (restRes.value.data.data.restaurants || []) : []);
      setAllPartners(partRes.status === 'fulfilled' ? (partRes.value.data.data.deliveryPartners || []) : []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load finance data');
    } finally { setLoading(false); }
  }, [params]);

  const loadExpenses = useCallback(async (page = 1) => {
    setLoading(true); setError('');
    try {
      const res = await expenseAPI.getExpenses({ ...params(), ...filters, page, limit: pagination.limit });
      setExpenses(res.data.data.expenses);
      setPagination(res.data.data.pagination);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load expenses');
    } finally { setLoading(false); }
  }, [params, filters, pagination.limit]);

  const loadMonthly = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await adminAPI.getMonthlyRevenue({ ...params(), projectMonths });
      setMonthlyReport(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load monthly report');
      setMonthlyReport(null);
    } finally { setLoading(false); }
  }, [params, projectMonths]);

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'analytics') loadSummary();
    if (activeTab === 'expenses') loadExpenses(1);
    if (activeTab === 'monthly') loadMonthly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, range, filters, projectMonths]);

  const platformRevenue = dashboard?.overview?.platformEarnings ?? dashboard?.platformCommissions ?? null;
  const totalExpenses = summary?.totalExpenses ?? 0;
  const netProfit = platformRevenue != null ? platformRevenue - totalExpenses : null;
  const margin = platformRevenue ? (netProfit / platformRevenue) * 100 : null;

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); setMsg(''); };
  const openEdit = (e) => {
    setForm({
      category: e.category, subcategory: e.subcategory || '', description: e.description || '',
      amount: e.amount, gstApplicable: !!e.gstApplicable, gstRate: e.gstRate || '', gstAmount: e.gstAmount || '',
      vendorName: e.vendor?.name || '', vendorGstin: e.vendor?.gstin || '',
      paymentMethod: e.paymentMethod, paymentStatus: e.paymentStatus,
      expenseDate: new Date(e.expenseDate).toISOString().slice(0, 10), notes: e.notes || '',
      isRecurring: !!e.recurring?.isRecurring, frequency: e.recurring?.frequency || 'monthly'
    });
    setEditingId(e._id); setShowForm(true); setMsg('');
  };

  const submitForm = async (ev) => {
    ev.preventDefault();
    setSaving(true); setMsg('');
    const payload = {
      category: form.category, subcategory: form.subcategory, description: form.description,
      amount: Number(form.amount), gstApplicable: form.gstApplicable,
      gstRate: Number(form.gstRate) || 0, gstAmount: form.gstApplicable ? (Number(form.gstAmount) || 0) : 0,
      vendor: { name: form.vendorName, gstin: form.vendorGstin },
      paymentMethod: form.paymentMethod, paymentStatus: form.paymentStatus,
      expenseDate: form.expenseDate, notes: form.notes,
      recurring: { isRecurring: form.isRecurring, frequency: form.frequency }
    };
    try {
      if (editingId) await expenseAPI.update(editingId, payload);
      else await expenseAPI.create(payload);
      setShowForm(false);
      setMsg(editingId ? 'Expense updated' : 'Expense recorded');
      loadExpenses(pagination.page);
    } catch (e) {
      setMsg(e.response?.data?.message || 'Failed to save expense');
    } finally { setSaving(false); }
  };

  const voidExpense = async (id) => {
    const reason = window.prompt('Reason for voiding this expense?');
    if (reason === null) return;
    try {
      await expenseAPI.void(id, reason);
      setMsg('Expense voided');
      loadExpenses(pagination.page);
    } catch (e) { setMsg(e.response?.data?.message || 'Failed to void'); }
  };

  const doExport = async () => {
    try {
      const res = await expenseAPI.export({ ...params(), ...filters });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `expenses_${range.startDate}_to_${range.endDate}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setMsg('Export failed'); }
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 4 }}>💰 Finance Hub</h1>
        <p style={{ color: '#777', marginTop: 0 }}>Platform revenue, expenses, and net profit — date-wise.</p>

        {/* Date range (shared across tabs) */}
        <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666' }}>From</label>
            <input type="date" value={range.startDate} onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666' }}>To</label>
            <input type="date" value={range.endDate} onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['overview', 'monthly', 'expenses', 'analytics'].map((t) => (
            <button key={t} onClick={() => setActiveTab(t)}
              style={{ ...btn(activeTab === t ? '#FF6961' : '#eee'), color: activeTab === t ? '#fff' : '#333', textTransform: 'capitalize' }}>
              {t === 'overview' ? 'P&L Overview' : t === 'monthly' ? '📈 Monthly & Growth' : t}
            </button>
          ))}
        </div>

        {error && <div style={{ ...card, borderColor: '#EF4444', color: '#EF4444', marginBottom: 16 }}>{error}</div>}
        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669', marginBottom: 16 }}>{msg}</div>}
        {loading && <div style={{ padding: 16 }}>🔄 Loading…</div>}

        {/* ── P&L OVERVIEW ── */}
        {activeTab === 'overview' && !loading && (
          <div>
            {/* Headline P&L */}
            <KpiGrid items={[
              { label: 'Platform Revenue', value: platformRevenue != null ? fmt(platformRevenue) : '—', sub: 'platform earnings' },
              { label: 'Total Expenses', value: fmt(totalExpenses), color: '#EF4444', sub: `${summary?.count || 0} expenses · GST ${fmt(summary?.totalGst)}` },
              { label: 'Net Profit', value: netProfit != null ? fmt(netProfit) : '—', color: netProfit != null && netProfit >= 0 ? '#059669' : '#EF4444', sub: margin != null ? `${margin.toFixed(1)}% margin` : 'revenue unavailable' },
              { label: 'Pending Expenses', value: fmt(summary?.pendingExpenses), color: '#D97706', sub: `${summary?.pendingCount || 0} unpaid` }
            ]} />
            <div style={{ height: 6 }} />

            {dashboard ? (
              <>
                <Section title="Revenue & Orders">
                  <KpiGrid items={[
                    { label: 'Total Revenue (GMV)', value: fmt(dashboard.overview?.totalRevenue) },
                    { label: 'Delivered Orders', value: (dashboard.overview?.totalOrders ?? 0).toLocaleString('en-IN'), sub: `${(dashboard.overview?.ordersPlaced ?? 0).toLocaleString('en-IN')} placed · ${(dashboard.overview?.ordersCancelled ?? 0).toLocaleString('en-IN')} cancelled` },
                    { label: 'Avg Order Value', value: fmt(dashboard.overview?.averageOrderValue) },
                    { label: 'Platform Earnings', value: fmt(dashboard.overview?.platformEarnings), color: '#059669' }
                  ]} />
                </Section>

                {dashboard.overview?.platformEarningsBreakdown && (
                  <Section title="Platform Earnings Breakdown">
                    <KpiGrid items={[
                      { label: 'Delivery Fees Collected', value: fmt(dashboard.overview.platformEarningsBreakdown.deliveryFees) },
                      { label: 'Platform Fees', value: fmt(dashboard.overview.platformEarningsBreakdown.platformFees) },
                      { label: 'Settlement Fees (kept from payouts)', value: fmt(dashboard.overview.platformEarningsBreakdown.settlementFees) },
                      { label: 'Total Platform Earnings', value: fmt(dashboard.overview.platformEarnings), color: '#059669' }
                    ]} />
                  </Section>
                )}

                {dashboard.overview?.platformDiscountsGiven != null && (
                  <Section title="Platform Discounts Given">
                    <KpiGrid items={[
                      { label: 'Discounts Funded by Platform', value: fmt(dashboard.overview.platformDiscountsGiven), color: '#EF4444', sub: 'platform coupons + platform item discounts + platform share of shared coupons' },
                      { label: 'Platform Earnings', value: fmt(dashboard.overview.platformEarnings), color: '#059669' },
                      { label: 'Net Platform Earnings (after discounts)', value: fmt(dashboard.overview.platformEarningsNet), color: (dashboard.overview.platformEarningsNet ?? 0) >= 0 ? '#059669' : '#EF4444' }
                    ]} />
                  </Section>
                )}

                <Section title="Earnings Distribution">
                  <KpiGrid items={[
                    { label: 'Restaurant Payouts', value: fmt(dashboard.restaurantEarnings) },
                    { label: 'Delivery Payouts', value: fmt(dashboard.deliveryEarnings) },
                    { label: 'Platform Earnings', value: fmt(dashboard.platformCommissions), color: '#059669' }
                  ]} />
                </Section>

                <Section title="Payouts">
                  <KpiGrid items={[
                    { label: 'Total Paid Out', value: fmt(dashboard.payouts?.total) },
                    { label: 'Restaurant Payouts', value: fmt(dashboard.payouts?.restaurantPayouts) },
                    { label: 'Delivery Payouts', value: fmt(dashboard.payouts?.deliveryPayouts) },
                    dashboard.payouts?.successRate != null ? { label: 'Payout Success Rate', value: `${Number(dashboard.payouts.successRate).toFixed(1)}%` } : null
                  ]} />
                </Section>

                <Section title="Taxes (collected / deducted)">
                  <KpiGrid items={[
                    { label: 'GST Collected', value: fmt(dashboard.taxes?.totalGstCollected) },
                    dashboard.taxes?.gstBreakdown ? { label: 'CGST', value: fmt(dashboard.taxes.gstBreakdown.cgst) } : null,
                    dashboard.taxes?.gstBreakdown ? { label: 'SGST', value: fmt(dashboard.taxes.gstBreakdown.sgst) } : null,
                    dashboard.taxes?.gstBreakdown ? { label: 'IGST', value: fmt(dashboard.taxes.gstBreakdown.igst) } : null,
                    { label: 'TDS Deducted', value: fmt(dashboard.taxes?.totalTdsDeducted) }
                  ]} />
                </Section>

                <Section title="Period Snapshots">
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    {[['Today', dashboard.today], ['This Week', dashboard.thisWeek], ['This Month', dashboard.thisMonth]].map(([label, s]) => (
                      <div style={card} key={label}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>Revenue: <b>{fmt(s?.revenue)}</b></div>
                        <div style={{ fontSize: 12, color: '#666' }}>Orders: <b>{(s?.orders ?? 0).toLocaleString('en-IN')}</b></div>
                        <div style={{ fontSize: 12, color: '#666' }}>Platform: <b>{fmt(s?.platformEarnings)}</b></div>
                      </div>
                    ))}
                  </div>
                </Section>

              </>
            ) : (
              <div style={{ ...card, color: '#999' }}>Platform revenue data unavailable for this range — expenses are still shown above.</div>
            )}

            <Section title={`All Restaurants (${allRestaurants.length})`}>
              <div style={{ ...card, padding: 0, maxHeight: 360, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>#</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Restaurant</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Orders</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Revenue</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Avg Order</th>
                  </tr></thead>
                  <tbody>
                    {allRestaurants.length === 0 && <tr><td style={td} colSpan={5}>No restaurant orders in this range.</td></tr>}
                    {allRestaurants.map((r, i) => (
                      <tr key={r._id || i}>
                        <td style={td}>{i + 1}</td>
                        <td style={td}>{r.name}</td>
                        <td style={td}>{(r.totalOrders ?? 0).toLocaleString('en-IN')}</td>
                        <td style={td}><b>{fmt(r.totalRevenue)}</b></td>
                        <td style={td}>{fmt(r.averageOrderValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title={`All Delivery Partners (${allPartners.length})`}>
              <div style={{ ...card, padding: 0, maxHeight: 360, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>#</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Partner</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Deliveries</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Earnings</th>
                    <th style={{ ...th, position: 'sticky', top: 0, background: '#fff' }}>Avg / Delivery</th>
                  </tr></thead>
                  <tbody>
                    {allPartners.length === 0 && <tr><td style={td} colSpan={5}>No deliveries in this range.</td></tr>}
                    {allPartners.map((d, i) => (
                      <tr key={d._id || i}>
                        <td style={td}>{i + 1}</td>
                        <td style={td}>{d.name}</td>
                        <td style={td}>{(d.totalDeliveries ?? 0).toLocaleString('en-IN')}</td>
                        <td style={td}><b>{fmt(d.totalEarnings)}</b></td>
                        <td style={td}>{fmt(d.averagePerDelivery)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {summary?.byCategory?.length > 0 && (
              <Section title="Expenses by Category">
                <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Category</th><th style={th}>Count</th><th style={th}>Total</th><th style={th}>% of Expenses</th></tr></thead>
                    <tbody>
                      {summary.byCategory.map((c) => (
                        <tr key={c.category}>
                          <td style={td}>{catLabel(c.category)}</td>
                          <td style={td}>{c.count}</td>
                          <td style={td}><b>{fmt(c.total)}</b></td>
                          <td style={td}>{totalExpenses > 0 ? `${((c.total / totalExpenses) * 100).toFixed(1)}%` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            <p style={{ fontSize: 11, color: '#aaa' }}>
              Net Profit = Platform Revenue − Total Expenses. Deeper revenue/tax/GSTR/TDS reports live in Finance Audit & CA Audit Reports.
            </p>
          </div>
        )}

        {/* ── EXPENSES ── */}
        {activeTab === 'expenses' && (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                <option value="">All categories</option>
                {Object.keys(CATEGORY_LABELS).map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Active</option>
                <option value="void">Voided</option>
              </select>
              <button onClick={openAdd} style={btn('#FF6961')}>+ Add Expense</button>
              <button onClick={doExport} style={btn('#6366F1')}>⬇ Export CSV</button>
            </div>

            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Date</th><th style={th}>Category</th><th style={th}>Description</th>
                    <th style={th}>Vendor</th><th style={th}>Amount</th><th style={th}>Total</th>
                    <th style={th}>Payment</th><th style={th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 && <tr><td style={td} colSpan={8}>No expenses in this range.</td></tr>}
                  {expenses.map((e) => (
                    <tr key={e._id} style={{ opacity: e.status === 'void' ? 0.5 : 1 }}>
                      <td style={td}>{new Date(e.expenseDate).toLocaleDateString('en-IN')}</td>
                      <td style={td}>{catLabel(e.category)}{e.subcategory ? ` · ${e.subcategory}` : ''}</td>
                      <td style={td}>{e.description || '—'}</td>
                      <td style={td}>{e.vendor?.name || '—'}</td>
                      <td style={td}>{fmt(e.amount)}</td>
                      <td style={td}><b>{fmt(e.totalAmount)}</b></td>
                      <td style={td}>
                        <span style={{ fontSize: 12, color: e.paymentStatus === 'paid' ? '#059669' : '#D97706' }}>
                          {e.paymentStatus}
                        </span><br /><span style={{ fontSize: 11, color: '#999' }}>{e.paymentMethod}</span>
                      </td>
                      <td style={td}>
                        {e.status === 'void'
                          ? <span style={{ fontSize: 12, color: '#EF4444' }}>voided</span>
                          : (<>
                              <button onClick={() => openEdit(e)} style={{ ...btn('#eee'), color: '#333', padding: '4px 10px', marginRight: 6 }}>Edit</button>
                              <button onClick={() => voidExpense(e._id)} style={{ ...btn('#EF4444'), padding: '4px 10px' }}>Void</button>
                            </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <button disabled={pagination.page <= 1} onClick={() => loadExpenses(pagination.page - 1)} style={btn('#eee')}>‹ Prev</button>
                <span style={{ fontSize: 13 }}>Page {pagination.page} / {pagination.pages} ({pagination.total} total)</span>
                <button disabled={pagination.page >= pagination.pages} onClick={() => loadExpenses(pagination.page + 1)} style={btn('#eee')}>Next ›</button>
              </div>
            )}
          </div>
        )}

        {/* ── MONTHLY & GROWTH ── */}
        {activeTab === 'monthly' && !loading && (
          monthlyReport ? (() => {
            const mr = monthlyReport;
            const proj = mr.projection || {};
            const chip = (v) => v == null
              ? <span style={{ color: '#9CA3AF' }}>—</span>
              : <span style={{ color: v >= 0 ? '#059669' : '#DC2626', fontWeight: 600 }}>{v >= 0 ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%</span>;
            const bars = [
              ...mr.months.map((m) => ({ label: m.label, gmv: m.gmv, partial: m.partial, projected: false })),
              ...((proj.months || []).map((p) => ({ label: p.label, gmv: p.gmv || 0, projected: true })))
            ];
            const maxV = Math.max(1, ...bars.map((b) => b.gmv));
            const W = Math.max(640, bars.length * 46), H = 200, pad = 28;
            const slot = (W - pad * 2) / bars.length, bw = Math.min(32, slot - 8);
            return (
              <div>
                <KpiGrid items={[
                  { label: `Total Revenue (GMV) · ${mr.summary.totalMonths} mo`, value: fmt(mr.totals.gmv) },
                  { label: 'Total Platform Earnings', value: fmt(mr.totals.platformEarnings), color: '#059669' },
                  { label: 'Median MoM Growth (earnings)', value: mr.summary.medianMoMGrowthPct != null ? `${mr.summary.medianMoMGrowthPct}%` : '—', color: (mr.summary.medianMoMGrowthPct || 0) >= 0 ? '#059669' : '#DC2626' },
                  { label: `Net Profit · ${mr.summary.totalMonths} mo`, value: fmt(mr.totals.netProfit), color: mr.totals.netProfit >= 0 ? '#059669' : '#DC2626' }
                ]} />
                <div style={{ height: 6 }} />

                <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>🔮 Project next</span>
                  <select value={projectMonths} onChange={(e) => setProjectMonths(Number(e.target.value))} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd' }}>
                    {[0, 1, 2, 3, 6, 12].map((n) => <option key={n} value={n}>{n} months</option>)}
                  </select>
                  {proj.available ? (
                    <span style={{ color: '#64748B', fontSize: 13 }}>
                      Trend <b style={{ color: proj.trend === 'up' ? '#059669' : proj.trend === 'down' ? '#DC2626' : '#64748B' }}>{proj.trend === 'up' ? '▲ growing' : proj.trend === 'down' ? '▼ declining' : '▬ flat'}</b>
                      {' · '}~{fmt(proj.monthlyPlatformEarningsSlope)}/mo change · projected next 3 mo earnings <b>{fmt(proj.next3PlatformEarnings)}</b>
                    </span>
                  ) : <span style={{ color: '#9CA3AF', fontSize: 13 }}>{proj.reason || 'Projection unavailable'}</span>}
                </div>

                <Section title="Monthly revenue (GMV) — solid = actual, light = projected">
                  <div style={{ overflowX: 'auto' }}>
                    <svg width={W} height={H + 40} style={{ display: 'block' }}>
                      {bars.map((b, i) => {
                        const x = pad + i * slot;
                        const h = Math.round((b.gmv / maxV) * H);
                        const y = H - h + 10;
                        const fill = b.projected ? '#C7D2FE' : (b.partial ? '#FCA5A5' : '#FF6961');
                        return (
                          <g key={i}>
                            <rect x={x} y={y} width={bw} height={Math.max(1, h)} rx={3} fill={fill}>
                              <title>{b.label}: {fmt(b.gmv)}{b.projected ? ' (projected)' : b.partial ? ' (partial month)' : ''}</title>
                            </rect>
                            <text x={x + bw / 2} y={H + 26} fontSize="9" textAnchor="middle" fill="#64748B" transform={`rotate(35 ${x + bw / 2} ${H + 26})`}>{b.label.replace(' 20', " '")}</text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                    <span style={{ color: '#FF6961' }}>■</span> actual &nbsp;
                    <span style={{ color: '#FCA5A5' }}>■</span> partial (current month) &nbsp;
                    <span style={{ color: '#C7D2FE' }}>■</span> projected
                  </div>
                </Section>

                <Section title="Monthly breakdown">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ textAlign: 'right', color: '#64748B', borderBottom: '2px solid #eee' }}>
                          <th style={{ textAlign: 'left', padding: '8px 6px' }}>Month</th>
                          <th style={{ padding: '8px 6px' }}>Orders</th>
                          <th style={{ padding: '8px 6px' }}>GMV</th>
                          <th style={{ padding: '8px 6px' }}>GMV g%</th>
                          <th style={{ padding: '8px 6px' }}>Platform Earnings</th>
                          <th style={{ padding: '8px 6px' }}>g%</th>
                          <th style={{ padding: '8px 6px' }}>Expenses</th>
                          <th style={{ padding: '8px 6px' }}>Net Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mr.months.map((m) => (
                          <tr key={m.month} style={{ textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600 }}>{m.label}{m.partial ? <span style={{ color: '#D97706', fontSize: 11 }}> (partial)</span> : ''}</td>
                            <td style={{ padding: '8px 6px' }}>{m.orders.toLocaleString('en-IN')}</td>
                            <td style={{ padding: '8px 6px' }}>{fmt(m.gmv)}</td>
                            <td style={{ padding: '8px 6px' }}>{chip(m.gmvGrowthPct)}</td>
                            <td style={{ padding: '8px 6px', color: '#059669' }}>{fmt(m.platformEarnings)}</td>
                            <td style={{ padding: '8px 6px' }}>{chip(m.platformEarningsGrowthPct)}</td>
                            <td style={{ padding: '8px 6px' }}>{fmt(m.expenses)}</td>
                            <td style={{ padding: '8px 6px', fontWeight: 600, color: m.netProfit >= 0 ? '#059669' : '#DC2626' }}>{fmt(m.netProfit)}</td>
                          </tr>
                        ))}
                        <tr style={{ textAlign: 'right', borderTop: '2px solid #eee', fontWeight: 700 }}>
                          <td style={{ textAlign: 'left', padding: '8px 6px' }}>Total</td>
                          <td style={{ padding: '8px 6px' }}>{mr.totals.orders.toLocaleString('en-IN')}</td>
                          <td style={{ padding: '8px 6px' }}>{fmt(mr.totals.gmv)}</td>
                          <td></td>
                          <td style={{ padding: '8px 6px', color: '#059669' }}>{fmt(mr.totals.platformEarnings)}</td>
                          <td></td>
                          <td style={{ padding: '8px 6px' }}>{fmt(mr.totals.expenses)}</td>
                          <td style={{ padding: '8px 6px', color: mr.totals.netProfit >= 0 ? '#059669' : '#DC2626' }}>{fmt(mr.totals.netProfit)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Section>

                {proj.available && proj.months && proj.months.length > 0 && (
                  <Section title={`🔮 Future projection — ${proj.method}`}>
                    <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>
                      Estimated from the trend of {proj.basedOnMonths} complete months (the partial current month is excluded so it doesn't bias the trend). Directional forecast — actuals will vary.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ textAlign: 'right', color: '#64748B', borderBottom: '2px solid #eee' }}>
                          <th style={{ textAlign: 'left', padding: '8px 6px' }}>Month (projected)</th>
                          <th style={{ padding: '8px 6px' }}>Projected GMV</th>
                          <th style={{ padding: '8px 6px' }}>Projected Platform Earnings</th>
                          <th style={{ padding: '8px 6px' }}>Projected Net Profit</th>
                        </tr></thead>
                        <tbody>
                          {proj.months.map((p) => (
                            <tr key={p.month} style={{ textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ textAlign: 'left', padding: '8px 6px', fontWeight: 600, color: '#4F46E5' }}>{p.label}</td>
                              <td style={{ padding: '8px 6px' }}>{fmt(p.gmv)}</td>
                              <td style={{ padding: '8px 6px', color: '#059669' }}>{fmt(p.platformEarnings)}</td>
                              <td style={{ padding: '8px 6px' }}>{fmt(p.netProfit)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}
              </div>
            );
          })() : (
            <div style={{ ...card, color: '#999' }}>No monthly data for this range.</div>
          )
        )}

        {/* ── ANALYTICS ── */}
        {activeTab === 'analytics' && !loading && summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            <div style={card}>
              <h3 style={{ marginTop: 0 }}>By Category</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Category</th><th style={th}>Count</th><th style={th}>Total</th></tr></thead>
                <tbody>
                  {summary.byCategory.length === 0 && <tr><td style={td} colSpan={3}>No data.</td></tr>}
                  {summary.byCategory.map((c) => (
                    <tr key={c.category}><td style={td}>{catLabel(c.category)}</td><td style={td}>{c.count}</td><td style={td}><b>{fmt(c.total)}</b></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={card}>
              <h3 style={{ marginTop: 0 }}>By Month</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Month</th><th style={th}>Count</th><th style={th}>Total</th></tr></thead>
                <tbody>
                  {summary.byMonth.length === 0 && <tr><td style={td} colSpan={3}>No data.</td></tr>}
                  {summary.byMonth.map((m) => (
                    <tr key={m.month}><td style={td}>{m.month}</td><td style={td}>{m.count}</td><td style={td}><b>{fmt(m.total)}</b></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ADD / EDIT MODAL ── */}
        {showForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <form onSubmit={submitForm} style={{ ...card, width: 560, maxHeight: '90vh', overflowY: 'auto' }}>
              <h2 style={{ marginTop: 0 }}>{editingId ? 'Edit Expense' : 'Add Expense'}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label>Category
                  <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ width: '100%' }}>
                    {Object.keys(CATEGORY_LABELS).map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                  </select>
                </label>
                <label>Subcategory
                  <input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label>Amount (₹)
                  <input type="number" step="0.01" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label>Date
                  <input type="date" required value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>Description
                  <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label>Vendor name
                  <input value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label>Vendor GSTIN
                  <input value={form.vendorGstin} onChange={(e) => setForm({ ...form, vendorGstin: e.target.value })} style={{ width: '100%' }} />
                </label>
                <label>Payment method
                  <select value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} style={{ width: '100%' }}>
                    {['bank_transfer', 'upi', 'card', 'cash', 'cheque', 'auto_debit', 'other'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label>Payment status
                  <select value={form.paymentStatus} onChange={(e) => setForm({ ...form, paymentStatus: e.target.value })} style={{ width: '100%' }}>
                    <option value="paid">paid</option><option value="pending">pending</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.gstApplicable} onChange={(e) => setForm({ ...form, gstApplicable: e.target.checked })} /> GST applicable
                </label>
                {form.gstApplicable && (
                  <label>GST amount (₹)
                    <input type="number" step="0.01" min="0" value={form.gstAmount} onChange={(e) => setForm({ ...form, gstAmount: e.target.value })} style={{ width: '100%' }} />
                  </label>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.isRecurring} onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })} /> Recurring
                </label>
                {form.isRecurring && (
                  <label>Frequency
                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} style={{ width: '100%' }}>
                      {['weekly', 'monthly', 'quarterly', 'yearly'].map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </label>
                )}
                <label style={{ gridColumn: '1 / -1' }}>Notes
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ width: '100%' }} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)} style={{ ...btn('#eee'), color: '#333' }}>Cancel</button>
                <button type="submit" disabled={saving} style={btn('#FF6961')}>{saving ? 'Saving…' : (editingId ? 'Update' : 'Save')}</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default FinanceHub;
